import React, { useCallback, useState } from "react";
import {
  View,
  ScrollView,
  ActivityIndicator,
  RefreshControl,
  StyleSheet,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import Text from "@components/CustomText";
import color from "@constants/color";
import Header from "../components/Header";
import PackingDBCard from "../components/PackingDBCard";
import ProgressBar from "../components/ProgressBar";
import TabBar from "../components/TabBar";
import CratePackingTab from "./CratePackingTab";
import useRoutingData from "../hooks/useRoutingData";
import usePackingDbs from "../hooks/usePackingDbs";
import { deriveReadyDbs } from "../utils";
import { tokens, sharedStyles } from "../styles";

// Item Packing — landing for the packing flow. Two tabs:
//   • DB-wise Packing  → list of DBs, each card shows their packing %;
//     tapping drills into PackingInterfaceScreen.
//   • Crate-wise Packing → mirrors web's Customer Crate Items page: type
//     or scan a crate, fetch it, pack/mark items + connecting crates.
// Both tabs share the same underlying writes — packing from either
// reflects on the other after a reload.

const TABS = [
  { key: "db", label: "DB-wise Packing" },
  { key: "crate", label: "Crate-wise Packing" },
];

const PackingDbListScreen = ({ navigation }) => {
  const { data, loading, reload } = useRoutingData();
  const dbs = usePackingDbs(data);
  const [tab, setTab] = useState("db");

  // Refetch on every focus — including return-from-PackingInterfaceScreen
  // after a pack-visible / unpack-visible / mark write. Without this the
  // list keeps showing the pre-pack snapshot (the interface screen has
  // its own useRoutingData state that updates locally on reload, but this
  // screen's state stays stale until manually pulled-to-refresh).
  useFocusEffect(
    useCallback(() => {
      reload();
    }, [reload]),
  );

  // Overall Progress = packed_customers / total_customers across every
  // assigned customer at this CP. Same numerator/denominator the per-DB
  // bars use, so the header bar never disagrees with the DB cards.
  // "Ready DBs" stays as informational meta (DBs at 100% packed).
  const totalCustomers = dbs.reduce((s, d) => s + d.customers, 0);
  const totalPacked = dbs.reduce((s, d) => s + d.packed, 0);
  const overallPct =
    totalCustomers > 0 ? Math.round((totalPacked / totalCustomers) * 100) : 0;
  const { active: dbsWithCustomers, ready: readyDbs } = deriveReadyDbs(dbs);

  return (
    <View style={sharedStyles.screen}>
      <Header
        title="Item Packing"
        subtitle={
          tab === "db"
            ? "Select a delivery boy to start packing"
            : "Fetch a crate to pack its items"
        }
        onBack={() => navigation.goBack()}
      />
      <TabBar tabs={TABS} activeKey={tab} onChange={setTab} />
      {tab === "db" ? (
        loading && !data ? (
          <View style={sharedStyles.centered}>
            <ActivityIndicator color={color.mediumBlack} />
          </View>
        ) : (
          <ScrollView
            contentContainerStyle={styles.scroll}
            refreshControl={
              <RefreshControl
                refreshing={loading}
                onRefresh={reload}
                tintColor={color.mediumBlack}
              />
            }
          >
            <View style={[sharedStyles.card, styles.overview]}>
              <View style={styles.overviewRow}>
                <Text variant="caption_2" style={styles.overviewLabel}>
                  Overall Progress
                </Text>
                <Text variant="title_16" style={styles.overviewPct}>
                  {overallPct}%
                </Text>
              </View>
              <ProgressBar percent={overallPct} height={10} />
              <Text variant="caption_2" style={styles.overviewMeta}>
                {readyDbs} / {dbsWithCustomers.length} DBs ready · {totalPacked}{" "}
                / {totalCustomers} customers packed
              </Text>
            </View>
            {dbs.length === 0 ? (
              <Text variant="caption_2" style={sharedStyles.emptyText}>
                No delivery boys at this CP yet.
              </Text>
            ) : (
              dbs.map((db) => (
                <PackingDBCard
                  key={String(db.cp_db_id)}
                  db={db}
                  onPress={(d) =>
                    navigation.navigate("PackingInterfaceScreen", {
                      cp_db_id: d.cp_db_id,
                      db_name: d.db_name,
                    })
                  }
                />
              ))
            )}
          </ScrollView>
        )
      ) : (
        <CratePackingTab parentReload={reload} />
      )}
    </View>
  );
};

export default PackingDbListScreen;

const styles = StyleSheet.create({
  scroll: {
    paddingTop: tokens.spacing.medium,
    paddingBottom: tokens.spacing.xlarge,
  },
  overview: { paddingVertical: tokens.spacing.large },
  overviewRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: tokens.spacing.small,
  },
  overviewLabel: {
    color: tokens.textSecondary,
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  overviewPct: {
    color: tokens.textPrimary,
    fontWeight: "700",
    fontSize: 18,
  },
  overviewMeta: {
    color: tokens.textSecondary,
    fontSize: 12,
    marginTop: tokens.spacing.medium,
  },
});
